import React from 'react';
import PageTitle from '../layout/PageTitle';
import { BarChart3, MessageSquare, Zap, Cpu, TrendingUp } from 'lucide-react';
import AppName from '../ui/AppName';

const iconBoxStyle: React.CSSProperties = {
  background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.22) 0%, rgba(212, 175, 55, 0.08) 100%)',
  border: '1px solid rgba(212, 175, 55, 0.25)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 4px 12px rgba(0,0,0,0.25), 0 0 20px rgba(212, 175, 55, 0.08)',
};

const AboutProject: React.FC = () => {
  return (
    <div className="content">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <PageTitle title="About SentiHype" />

        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            What is <AppName className="text-inherit font-black" />?
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mx-auto">
            <AppName className="text-inherit" /> aggregates social sentiment about stocks and cryptocurrencies from Reddit. 
            We track what the online community is discussing — the noise, opinions, and hype — and turn it into structured data.
          </p>
        </div>

        {/* What we do */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">What We Do</h2>
          <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6 md:p-8">
            <p className="text-white/80 leading-relaxed mb-4">
              <AppName className="text-inherit" /> analyzes posts and comments from Reddit (r/wallstreetbets, r/stocks, r/investing, and 50+ other finance subreddits). AI models process the text, extract sentiment, and calculate scores for each ticker — not just keyword counting, but understanding context and market-specific language.
            </p>
            <p className="text-white/80 leading-relaxed">
              The result: trending tickers, sentiment charts, WSS (Weighted Sentiment Score), and correlation indices. Use it as one input among many — not investment advice, but a structured view of what the community is discussing.
            </p>
          </div>
        </div>

        {/* How it works - compelling tech */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">How It Works</h2>
          <div className="space-y-6">
            <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6 flex gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform hover:scale-105" style={iconBoxStyle}>
                <Cpu className="w-6 h-6 text-brand-gold about-project-icon-shadow" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">AI-Powered Sentiment Analysis</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  We use natural language processing (NLP) and machine learning models to analyze thousands of Reddit posts and comments every day. The AI extracts sentiment — bullish, bearish, or neutral — and assigns scores to each ticker. This is not simple keyword counting; it understands context, sarcasm, and market-specific language.
                </p>
              </div>
            </div>
            <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6 flex gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform hover:scale-105" style={iconBoxStyle}>
                <TrendingUp className="w-6 h-6 text-brand-gold about-project-icon-shadow" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">WSS: Weighted Sentiment Score</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Our proprietary WSS algorithm doesn't treat all mentions equally. It weighs sentiment by author credibility, post engagement, historical accuracy, and activity patterns. The result: a more meaningful signal that filters out noise and highlights actionable sentiment shifts. Premium users get full WSS breakdowns with spike detection and anomaly alerts.
                </p>
              </div>
            </div>
            <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6 flex gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform hover:scale-105" style={iconBoxStyle}>
                <BarChart3 className="w-6 h-6 text-brand-gold about-project-icon-shadow" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Correlation Indices</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  We calculate correlation between mention volume and sentiment, and (for Premium) between social hype and price movement. These indices help you spot when sentiment diverges from price — a signal many traders use to time entries. See which tickers have the strongest sentiment–price correlation over different timeframes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Key points */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform hover:scale-105" style={iconBoxStyle}>
              <MessageSquare className="w-6 h-6 text-brand-gold about-project-icon-shadow" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Community Noise</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              We analyze public discussions — opinions, emotions, speculation. This is social sentiment, not financial advice.
            </p>
          </div>
          <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform hover:scale-105" style={iconBoxStyle}>
              <BarChart3 className="w-6 h-6 text-brand-gold about-project-icon-shadow" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Aggregated Data</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              AI models process thousands of posts daily. We aggregate mentions, sentiment, and trends for each ticker.
            </p>
          </div>
          <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform hover:scale-105" style={iconBoxStyle}>
              <Zap className="w-6 h-6 text-brand-gold about-project-icon-shadow" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">FREE & Premium</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              FREE: data delayed by 6 hours. Premium: real-time data, 30 days history, full WSS, price correlation.
            </p>
          </div>
        </div>

        {/* Who is it for */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Who Is It For?</h2>
          <div className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl p-6 md:p-8">
            <p className="text-white/80 leading-relaxed mb-4">
              <AppName className="text-inherit" /> is for anyone curious about what Reddit and the broader community are saying about stocks and crypto. People use it to:
            </p>
            <ul className="list-disc pl-6 text-white/80 space-y-2">
              <li>See which tickers are trending in online discussions</li>
              <li>Track sentiment changes over time</li>
              <li>Spot spikes in mentions or mood</li>
              <li>Get a sense of the "hype" around specific assets</li>
            </ul>
            <p className="text-white/60 text-sm mt-4">
              Use it as one of many inputs. Always do your own research.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutProject;
